/**
 * SdmsBatchResultsPopup — the SDMS ribbon's "QC Batch Results" action.
 *
 * OG (`SpreadsheetEntryController.cs` :9377, `QCbatchIDResults`) opens a modal
 * list of the batch's per-(sample x analyte) results (`SDMSDCAB_ListView_Copy`).
 * Here it's a read-only popup fed by the SAME endpoint the Result-Validation
 * package view already uses -- `GET .../result-validation/{oid}/batch-results` --
 * so there's one source for batch results, not two (CLAUDE.md rule 8). The SDMS
 * call passes `fillLimits=true` so QC-type rows show the analyte's RL/MDL/LOQ/UQL
 * too; Result Validation calls the same endpoint WITHOUT it, so its grid is
 * untouched (scoped per the "only-current-form" rule).
 *
 * Read-only by design: this is Phase 1 (view only). The editable Batch-Results
 * grid + save lives in the Result-Validation flow; entering/saving results from
 * SDMS itself is a later phase.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { ExportMenu } from '../../components/AppToolbar/ExportMenu';
import { useNotify } from '../../components/notifications';
import { qcBatchApi, type SdmsBatchResult } from './qcBatch.api';
import { formatSdmsNumeric } from './sdmsNumericFormat';
import './sdmsEntry.css';

// OG display-rounds NumericResult to the row's Decimal places and trims trailing zeros
// (e.g. 2.4972 -> 2.5, -0.0020 -> 0). Result/RPD/etc. keep their stored form. (2026-09-03)
// The rule lives in sdmsNumericFormat.ts (shared with Result Tracking); 2 places when unset.
const fmtNum = (v: string | null, dec: string | null): string => formatSdmsNumeric(v, dec, 2);

type TplCol = { fieldName: string; caption: string; sort: number };
// Maps a template FieldSetUp storage field name -> the batch-results row property
// (+ how to render it). Fields with no entry (e.g. UserDefinedN template columns) render
// blank, matching OG when the sheet has no value for them.
type FieldMap = { prop?: keyof SdmsBatchResult; type?: 'datetime' | 'status'; num?: boolean; dup?: boolean; batchId?: boolean };
const FIELD_MAP: Record<string, FieldMap> = {
  QCType: { prop: 'qcTypeName' }, RunType: { prop: 'qcTypeName', dup: true },
  ClientSampleID: { prop: 'sampleName' }, SampleName: { prop: 'sampleName', dup: true },
  VolumeUsed: { prop: 'volumeUsed' }, FinalVolume: { prop: 'finalVolume' },
  Result: { prop: 'result' }, AnalyzedBy: { prop: 'analyzedBy' }, AnalyzedDate: { prop: 'analyzedDate', type: 'datetime' },
  Parameter: { prop: 'parameter' }, SystemID: { prop: 'systemId' }, RunNo: { prop: 'runNo' }, SampleID: { prop: 'sampleId' },
  DF: { prop: 'df' }, RptLimit: { prop: 'rptLimit' }, MDL: { prop: 'mdl' }, LOQ: { prop: 'loq' }, UQL: { prop: 'uql' },
  Units: { prop: 'units' }, SpikeAmount: { prop: 'spikeAmount' }, Qualifier: { prop: 'qualifier' },
  '%Recovery': { prop: 'rec' }, RPD: { prop: 'rpd' }, NumericResult: { prop: 'numericResult', num: true },
  SiteName: { prop: 'siteName' }, '%RECCTRLLIMIT': { prop: 'recCtrlLimitText' }, '%RDCTRLLIMIT': { prop: 'rpdCtrlLimitText' },
  Test: { prop: 'testName' }, Method: { prop: 'methodName' }, Matrix: { prop: 'matrixName' }, Instrument: { prop: 'seInstrument' },
  SysSampleCode: { prop: 'sys' }, Dilution: { prop: 'dilution' }, MCL: { prop: 'mcl' }, DLD: { prop: 'dld' }, QCStatus: { prop: 'qcStatus', type: 'status' },
  CalibrationID: { prop: 'calibrationIdText' }, CalibratedBy: { prop: 'calibratedBy' },
  CalibrationDate: { prop: 'calibratedDate', type: 'datetime' }, CalibratedDate: { prop: 'calibratedDate', type: 'datetime' },
  InternalStandardLink: { prop: 'internalStandardLinkText' },
  '[%RECLLIMIT]': { prop: 'recLLimit' }, '[%RECULIMIT]': { prop: 'recULimit' },
  '[%RPDLLIMIT]': { prop: 'rpdLLimit' }, '[%RPDULIMIT]': { prop: 'rpdULimit' }, JobID: { prop: 'jobId' },
  HighCLimit: { prop: 'highCLimit' }, LowCLimit: { prop: 'lowCLimit' },
  DefaultResult: { prop: 'defaultResult' }, SampleLocation: { prop: 'sampleLocation' }, SAMPLELOCATION: { prop: 'sampleLocation' },
  ProjectName: { prop: 'projectName' }, PROJECTNAME: { prop: 'projectName' },
  CollectionDate: { prop: 'dateCollected', type: 'datetime' }, EnteredBy: { prop: 'enteredBy' }, EnteredDate: { prop: 'enteredDate', type: 'datetime' },
  QCSampleResult: { prop: 'qcSampleResult' }, DupSource: { prop: 'dupSource' }, DupSourceResult: { prop: 'dupSourceResult' },
  SpikeSource: { prop: 'spikeSource' }, SpikeSourceResult: { prop: 'spikeSourceResult' },
  SigFig: { prop: 'sigFig' }, CutOff: { prop: 'cutOff' }, Decimal: { prop: 'decimalPlaces' }, IsReport: { prop: 'isReport' },
  QCBatchID: { batchId: true }, LastUpdatedBy: { prop: 'lastUpdatedBy' }, LastUpdatedDate: { prop: 'lastUpdatedDate', type: 'datetime' },
};

// Generic passthrough for template fields that aren't a typed prop: the reader packs the
// remaining physical se columns into seExtrasJson keyed by se column name (bracket-stripped).
const _extrasCache = new WeakMap<object, Record<string, unknown>>();
function extraVal(r: SdmsBatchResult, key: string): string {
  let e = _extrasCache.get(r);
  if (!e) {
    try { e = r.seExtrasJson ? (JSON.parse(r.seExtrasJson) as Record<string, unknown>) : {}; }
    catch { e = {}; }
    _extrasCache.set(r, e);
  }
  const v = e[key];
  return v == null ? '' : String(v);
}

export function SdmsBatchResultsPopup({ batchOid, batchLabel, onClose }:
  { batchOid: string; batchLabel: string; onClose: () => void }) {
  const notify = useNotify();
  const [rows, setRows] = useState<SdmsBatchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tplCols, setTplCols] = useState<TplCol[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      qcBatchApi.sdmsBatchResults(batchOid),
      qcBatchApi.sdmsGridColumns(batchOid).catch(() => [] as TplCol[]),  // no template -> static fallback
    ])
      .then(([r, cols]) => { if (!cancelled) { setRows(r); setTplCols(cols); } })
      .catch(() => { if (!cancelled) notify.error('Could not load the batch results.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchOid]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const staticColumns = useMemo<AppColumn<SdmsBatchResult>[]>(() => [
    // Column order + names mirror OG's SDMSDCAB grid (QB26090112_042). Template-specific
    // fields (ColorUnit*, 5ppmColorSTDLL, rValue, CalExperation) and calibration columns
    // (CalibratedBy / CalibrationID) aren't produced by the shared reader, so they're omitted.
    { field: 'qcTypeName',       header: 'QCType', width: 140 },
    { field: 'sampleName',       header: 'SampleName' },
    { field: 'volumeUsed',       header: 'VolumeUsed' },
    { field: 'finalVolume',      header: 'FinalVolume' },
    { field: 'result',           header: 'Result' },
    { field: 'analyzedBy',       header: 'AnalyzedBy' },
    { field: 'analyzedDate',     header: 'AnalyzedDate', type: 'datetime' },
    { field: 'parameter',        header: 'Parameter' },
    { field: 'runType',          header: 'RunType', value: r => r.qcTypeName, text: r => r.qcTypeName ?? '' },
    { field: 'systemId',         header: 'SystemID' },
    { field: 'runNo',            header: 'RunNo' },
    { field: 'sampleId',         header: 'SampleID' },
    { field: 'df',               header: 'DF' },
    { field: 'rptLimit',         header: 'RptLimit' },
    { field: 'mdl',              header: 'MDL' },
    { field: 'loq',              header: 'LOQ' },
    { field: 'uql',              header: 'UQL' },
    { field: 'spikeAmount',      header: 'SpikeAmount' },
    { field: 'qualifier',        header: 'Qualifier' },
    { field: 'rec',              header: '%Recovery' },
    { field: 'rpd',              header: 'RPD' },
    { field: 'numericResult',    header: 'NumericResult', value: r => fmtNum(r.numericResult, r.decimalPlaces), text: r => fmtNum(r.numericResult, r.decimalPlaces) },
    { field: 'units',            header: 'Units' },
    { field: 'siteName',         header: 'SiteName' },
    { field: 'recCtrlLimit',     header: '%RecCtrlLimit' },
    { field: 'rpdCtrlLimit',     header: '%RDCtrlLimit' },
    { field: 'dateCollected',    header: 'CollectionDate', type: 'datetime' },
    { field: 'qcStatus',         header: 'QCStatus', type: 'status' },
    { field: 'enteredBy',        header: 'EnteredBy' },
    { field: 'enteredDate',      header: 'EnteredDate', type: 'datetime' },
    { field: 'qcSampleResult',   header: 'QC Sample Result' },
    { field: 'dupSource',        header: 'DupSource' },
    { field: 'dupSourceResult',  header: 'DupSourceResult' },
    { field: 'spikeSource',      header: 'SpikeSource' },
    { field: 'spikeSourceResult',header: 'SpikeSourceResult' },
    { field: 'sigFig',           header: 'SigFig' },
    { field: 'cutOff',           header: 'CutOff' },
    { field: 'decimalPlaces',    header: 'Decimal' },
    { field: 'isReport',         header: 'IsReport' },
    { field: 'client',           header: 'Client' },
    { field: 'projectId',        header: 'ProjectID' },
    { field: 'lastUpdatedBy',    header: 'LastUpdatedBy' },
    { field: 'lastUpdatedDate',  header: 'LastUpdatedDate', type: 'datetime' },
  ], []);

  // Template-driven columns (OG SDMSDCAB): when the batch has a template, render exactly
  // that template's FieldSetUp columns in Sort order (headers = Caption_EN), mapping each
  // to the batch-results value; unmapped template fields (UserDefinedN) render blank.
  // No template -> the static OG-standard set above.
  const columns = useMemo<AppColumn<SdmsBatchResult>[]>(() => {
    if (!tplCols.length) return staticColumns;
    const built = tplCols.map((c): AppColumn<SdmsBatchResult> => {
      const m = FIELD_MAP[c.fieldName];
      if (!m) {
        // Generic template storage columns (UserDefinedN / ITEMn) carry the template field's
        // value directly on the row (the reader selects UserDefined1-60). Read it dynamically;
        // blank when the reader has no such column (e.g. ITEMn not wired yet).
        const gp = /^(UserDefined|ITEM)\d+$/.test(c.fieldName)
          ? c.fieldName.charAt(0).toLowerCase() + c.fieldName.slice(1) : null;
        if (gp) return { field: c.fieldName, header: c.caption,
          value: r => ((r as unknown as Record<string, unknown>)[gp] ?? '') as string,
          text: r => String((r as unknown as Record<string, unknown>)[gp] ?? '') };
        // Not a typed prop and not UserDefinedN/ITEMn -> resolve from the seExtrasJson bag
        // (bracket-stripped se column name). Blank when the column has no value for this row.
        return { field: c.fieldName, header: c.caption,
          value: r => extraVal(r, c.fieldName.replace(/[[\]]/g, '')),
          text: r => extraVal(r, c.fieldName.replace(/[[\]]/g, '')) };
      }
      if (m.batchId) return { field: c.fieldName, header: c.caption, value: () => batchLabel, text: () => batchLabel };
      const p = m.prop as keyof SdmsBatchResult;
      if (m.num) return { field: c.fieldName, header: c.caption, value: r => fmtNum(r[p] as string | null, r.decimalPlaces), text: r => fmtNum(r[p] as string | null, r.decimalPlaces) };
      if (m.dup)  return { field: c.fieldName, header: c.caption, value: r => (r[p] ?? '') as string, text: r => String(r[p] ?? '') };
      const col: AppColumn<SdmsBatchResult> = { field: p as string, header: c.caption };
      if (m.type) col.type = m.type;
      return col;
    });
    // OG pins QCTYPE fixed-left (SpreadsheetEntryController.cs :13224). Pull the QCType
    // column to the front and give it a width so it freezes as column 1 (stickyColumns={1}).
    const qi = tplCols.findIndex(c => c.fieldName === 'QCType');
    if (qi >= 0) { const [q] = built.splice(qi, 1); if (q) { q.width = 140; built.unshift(q); } }
    return built;
  }, [tplCols, staticColumns, batchLabel]);

  // Fuzzy search across ALL columns -- a single box matching the query as a
  // case-insensitive substring anywhere in the row (every field value + the
  // derived Sample Type). Runs client-side on the in-memory rows.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => {
      const hay = Object.values(r).filter(v => v != null).join(' ');
      return hay.toLowerCase().includes(q);
    });
  }, [rows, search]);

  // Export reflects the current search (the grid's own column filters also export
  // via the shared gridExportMatrix on this set).
  const exp = useMemo(() => gridExportMatrix(filtered, columns), [filtered, columns]);

  // Freeze QCType only when it's actually the first column (OG pins QCTYPE alone).
  // A template with no QCType (e.g. 8075) must NOT freeze its first column.
  const stickyN = columns[0]?.field === 'qcTypeName' ? 1 : 0;

  return createPortal(
    <div className="sdms-brp-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sdms-brp sdms-results" role="dialog" aria-label="QC Batch Results">
        <div className="sdms-brp-hd">
          <span>QC Batch Results — {batchLabel}</span>
          <button className="sdms-brp-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="sdms-brp-tools">
          <input
            className="sdms-brp-search"
            placeholder="Search all columns…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <span className="sdms-brp-exp">
            <ExportMenu filename={`qc-batch-results-${batchLabel}`} headers={exp.headers} rows={exp.rows} />
          </span>
        </div>
        <div className="sdms-brp-body">
          <AppDataGrid<SdmsBatchResult>
            rows={filtered}
            columns={columns}
            getRowId={r => r.id ?? `${r.sys}|${r.parameter}`}
            loading={loading}
            stickyColumns={stickyN}
            emptyText="No results for this batch yet."
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
